import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { db, auth } from "../firebase.config";
import { collection, onSnapshot } from "firebase/firestore";
import DashboardTable from "../Components/DashboardTable";
import { useDispatch, useSelector } from "react-redux";
import Modal from "../Components/Modal";
import { addExhibits } from "../Redux/Exhibits/exhibitSlice";
import { GrDocumentText } from "react-icons/gr";
import { setLoading } from "../Redux/Loader/loadingSlice";

// ---------- DEBUG HELPERS ----------
const DBG = true;
const tag = (...args) =>
  DBG && console.log("%c[Dashboard]", "color:#7B61FF", ...args);
const tagWarn = (...args) => DBG && console.warn("[Dashboard]", ...args);
const tagErr = (...args) => DBG && console.error("[Dashboard]", ...args);

// Spinner behavior
const MIN_SPINNER_MS = 1100; // hold spinner at least this long after each turn-ON
const FALLBACK_MS = 6000; // safety: if server snapshot lags, force-hide
const REENABLE_COOLDOWN_MS = 1200; // **NEW**: block quick re-enable after a fresh hide (StrictMode remounts)

// Module-scope memory of the last hide time (survives StrictMode remounts)
let lastSpinnerHideAt = 0;

const Dashboard = ({ setReviewExhibitID }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const exhibits = useSelector((state) => state.exhibits);

  const [modalToggle, setModalToggle] = useState(false);
  const [deleteExhbitID, setDeleteExhibitId] = useState(null);

  // Refs for lifecycle & logging
  const runIdRef = useRef(0);
  const snapCountRef = useRef(0);
  const lastOnAtRef = useRef(0); // when spinner was last turned ON
  const isOnRef = useRef(false); // our local view of spinner state
  const haveServerRef = useRef(false); // saw first server-backed, no-pending snapshot
  const fallbackRef = useRef(null);
  const hideTimerRef = useRef(null);

  // track modal open state for spinner policy
  const modalOpenRef = useRef(false);

  // --- Spinner helpers ---
  const clearTimers = () => {
    if (fallbackRef.current) {
      clearTimeout(fallbackRef.current);
      fallbackRef.current = null;
    }
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const ensureSpinnerOn = (reason) => {
    if (modalOpenRef.current) {
      tag(`Spinner ON suppressed (modal open) (reason=${reason})`);
      return;
    }
    // Cancel pending hide
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }

    // **Cooldown guard** — avoid ON if we JUST hid (StrictMode remount)
    const sinceHide = performance.now() - lastSpinnerHideAt;
    if (sinceHide < REENABLE_COOLDOWN_MS) {
      tag(
        `Spinner ON suppressed by cooldown (${Math.round(
          sinceHide
        )}ms < ${REENABLE_COOLDOWN_MS}ms) (reason=${reason})`
      );
      return;
    }

    if (!isOnRef.current) {
      tag(`Spinner ON (reason=${reason})`);
      dispatch(setLoading(true));
      isOnRef.current = true;
    } else {
      tag(`Spinner already ON (reason=${reason})`);
    }

    lastOnAtRef.current = performance.now();

    // Reset fallback for this ON phase
    if (fallbackRef.current) clearTimeout(fallbackRef.current);
    fallbackRef.current = setTimeout(() => {
      tagWarn(`FALLBACK fired [run ${runIdRef.current}] → forcing hide`);
      scheduleHide("fallback");
    }, FALLBACK_MS);
  };

  const scheduleHide = (reason) => {
    if (!isOnRef.current) {
      tag(`scheduleHide(${reason}) ignored — spinner already OFF`);
      return;
    }
    const elapsed = performance.now() - lastOnAtRef.current;
    const delay = Math.max(0, MIN_SPINNER_MS - elapsed);
    tag(
      `scheduleHide(${reason}) in ${Math.round(delay)}ms (elapsed=${Math.round(
        elapsed
      )}ms)`
    );

    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);

    hideTimerRef.current = setTimeout(() => {
      requestAnimationFrame(() => {
        tag(`Spinner OFF now (reason=${reason})`);
        dispatch(setLoading(false));
        isOnRef.current = false;

        // **Remember hide time** to block immediate re-enable on StrictMode remounts
        lastSpinnerHideAt = performance.now();

        if (fallbackRef.current) {
          clearTimeout(fallbackRef.current);
          fallbackRef.current = null;
        }
      });
    }, delay);
  };

  // --- AUTH GUARD ---
  useEffect(() => {
    if (!auth.currentUser) {
      tagWarn("No auth.currentUser — redirecting to /");
      navigate("/");
    } else {
      tag("Auth OK:", auth.currentUser?.uid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- ENTRY: each time we land (or re-land) on Dashboard ---
  useEffect(() => {
    runIdRef.current += 1;
    const runId = runIdRef.current;

    // Do NOT reset haveServerRef here; a StrictMode remount shouldn’t cause policy changes.
    clearTimers();

    tag(`Route entry [run ${runId}] → request spinner ON`);
    ensureSpinnerOn("route-entry");

    requestAnimationFrame(() => {
      tag("Scroll to top");
      // use "auto" for instant; switch to "smooth" if you prefer an animation
      window.scrollTo({ top: 0, left: 0 });
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  // --- MODAL: ensure it's clickable (spinner OFF while modal open) ---
  useEffect(() => {
    modalOpenRef.current = modalToggle;

    if (modalToggle) {
      tag("Modal opened → kill global spinner immediately for clickability");
      scheduleHide("modal-open", { immediate: true });
      // Prevent spinner from turning back on from "pendingWrites" snapshots
      haveServerRef.current = true;
    } else {
      tag("Modal closed");
    }
  }, [modalToggle]);

  // --- FIRESTORE SUBSCRIPTION ---
  useEffect(() => {
    const exhibitsCollection = collection(db, "exhibits");
    tag("Subscribing to Firestore exhibits…");

    const unsubscribe = onSnapshot(
      exhibitsCollection,
      { includeMetadataChanges: true },
      (snapshot) => {
        snapCountRef.current += 1;
        const n = snapCountRef.current;

        const fromCache = snapshot.metadata.fromCache;
        const hasPendingWrites = snapshot.metadata.hasPendingWrites;
        tag(
          `SNAPSHOT #${n}: totalDocs=${snapshot.docs.length}, fromCache=${fromCache}, pendingWrites=${hasPendingWrites}`
        );

        const all = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        const normalize = (s) => (s || "").trim().toLowerCase();
        const companyName = normalize(user?.companyName);

        const filtered = all.filter(
          (ex) =>
            normalize(ex.companyName) === companyName && ex.status !== "Partner"
        );

        tag(
          `SNAPSHOT #${n}: filteredForCompany="${user?.companyName}" → ${filtered.length} items`
        );

        // --- helpers for sorting by most-recent ---
        const parseMDY = (s) => {
          if (!s) return new Date(0);
          const [m, d, y = "0"] = String(s).split("/");
          const yyyy = y.length === 2 ? 2000 + Number(y) : Number(y);
          return new Date(Number(yyyy), Number(m) - 1, Number(d));
        };

        const toMillis = (ts) => {
          if (!ts) return NaN;
          if (typeof ts.toMillis === "function") return ts.toMillis(); // Firestore Timestamp
          if (typeof ts.seconds === "number") {
            return ts.seconds * 1000 + Math.floor((ts.nanoseconds || 0) / 1e6); // plain object shape
          }
          if (ts instanceof Date) return ts.getTime();
          if (typeof ts === "string") {
            const t = Date.parse(ts);
            return Number.isNaN(t) ? NaN : t;
          }
          return NaN;
        };

        const getSortMs = (ex) => {
          const u = toMillis(ex.updatedAt);
          if (!Number.isNaN(u)) return u;
          const c = toMillis(ex.createdAt);
          if (!Number.isNaN(c)) return c;
          return parseMDY(ex.createdDate).getTime();
        };

        // --- sort: newest first by updatedAt (fallback createdAt/createdDate) ---
        const sorted = filtered.slice().sort((a, b) => {
          const aMs = getSortMs(a);
          const bMs = getSortMs(b);

          if (aMs !== bMs) return bMs - aMs; // newest first

          // tie-breaker: non-Submitted above Submitted
          if (a.status !== "Submitted" && b.status === "Submitted") return -1;
          if (a.status === "Submitted" && b.status !== "Submitted") return 1;

          return 0;
        });

        // (optional) debug: see which keys drove the sort
        tag(
          "SORT KEYS",
          sorted.map((ex) => ({
            id: ex.id,
            updatedAt: ex.updatedAt ?? null,
            createdAt: ex.createdAt ?? null,
            createdDate: ex.createdDate ?? null,
            ms: getSortMs(ex),
          }))
        );

        tag(`SNAPSHOT #${n}: dispatch(addExhibits(sorted[${sorted.length}]))`);
        dispatch(addExhibits(sorted));

        if (modalOpenRef.current) {
          tag(`SNAPSHOT #${n}: modal open → skip spinner policy changes`);
          return;
        }

        // Spinner policy:
        // 1) Before we've shown a server-backed snapshot, spinner should reflect pending writes.
        // 2) AFTER we've shown the first server-backed snapshot (no pending), we never re-enable
        //    the spinner for later pending writes → avoids flicker/hangs.
        if (!fromCache && !hasPendingWrites) {
          haveServerRef.current = true;
          tag(
            "%cSNAPSHOT: server-backed & no pending → request hide",
            "color:#0a0"
          );
          scheduleHide(`snapshot#${n}-server-noPending`);
        } else if (hasPendingWrites) {
          if (!haveServerRef.current) {
            tag(
              `SNAPSHOT #${n}: pending writes before first server snapshot → keep spinner ON`
            );
            ensureSpinnerOn("pendingWrites-pre-first-server");
          } else {
            tag(
              `SNAPSHOT #${n}: pending writes AFTER first server snapshot → leave spinner as-is (no flicker)`
            );
          }
        } else if (fromCache) {
          tag(`SNAPSHOT #${n}: from cache → keep spinner as-is`);
        }
      },
      (err) => {
        tagErr("Snapshot error:", err);
        scheduleHide("snapshot-error");
      }
    );

    return () => {
      tag("Unsubscribe Firestore and cleanup timers");
      unsubscribe();
      clearTimers();
      // Guard: ensure spinner is OFF when leaving this page
      dispatch(setLoading(false));
      isOnRef.current = false;
      // leave `lastSpinnerHideAt` intact (cooldown survives StrictMode remounts)
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [db, user?.companyName, dispatch]);

  // --- EXHIBITS CHANGE LOG ---
  useEffect(() => {
    tag(
      `Redux exhibits updated: length=${exhibits?.length ?? 0}`,
      exhibits?.[0]
        ? {
            firstId: exhibits[0].id,
            status: exhibits[0].status,
            createdDate: exhibits[0].createdDate,
          }
        : "(empty)"
    );
  }, [exhibits]);

  return (
    <>
      {modalToggle && (
        <Modal
          setModalToggle={(v) => {
            tag("Modal setModalToggle:", v);
            setModalToggle(v);
          }}
          deleteExhbitID={deleteExhbitID}
        />
      )}

      <div className="dashboard">
        <div className="title-btn-wrap">
          <h2>
            {`Welcome `}
            <span className="company-name">{`${user.companyName}`}</span>
          </h2>

          {/* Only show "Start A New Exhibit" when we actually have no rows */}
          {exhibits?.length < 1 && (
            <button
              className="dashboard-btn fresh new-exhibit-btn"
              type="button"
              onClick={() => {
                tag("Click: Start A New Exhibit → /exhibit");
                navigate("/exhibit");
              }}
            >
              Start A New Exhibit
              <GrDocumentText />
            </button>
          )}

          {exhibits?.length > 0 && (
            <button
              className="dashboard-btn new-exhibit-btn"
              type="button"
              onClick={() => {
                tag("Click: Start New Exhibit → /exhibit");
                navigate("/exhibit");
              }}
            >
              Start New Exhibit
            </button>
          )}
        </div>

        {exhibits?.length > 0 && (
          <DashboardTable
            // ↓ Centralized delete request handler: prevents scroll + blocks loader flicker
            onRequestDelete={(e, id) => {
              if (e) {
                e.preventDefault();
                e.stopPropagation();
              }
              tag("DashboardTable onRequestDelete:", id);

              // Block any spinner flicker while modal is open
              modalOpenRef.current = true;
              haveServerRef.current = true; // don't allow pending-writes to turn it back on
              if (isOnRef.current) scheduleHide("modal-open");
              dispatch(setLoading(false)); // hard off, immediately

              setDeleteExhibitId(id);
              setModalToggle(true);
            }}
            exhibits={exhibits}
            onReview={(id) => {
              tag("DashboardTable onReview:", id);
              setReviewExhibitID(id);
            }}
          />
        )}
      </div>
    </>
  );
};

export default Dashboard;
